/**
 * Which meals are open on a day, remembered per day.
 *
 * Meals collapse so the whole day fits one screen, which means the shape of
 * Today is now something the user arranges. Forgetting it on every visit would
 * make that arrangement a thing they redo four times an hour — Today is the
 * screen this app returns to more than any other, and it is returned to
 * *between* the acts of eating that change it.
 *
 * Per day and not per session, because the arrangement is about a day: dinner
 * open at seven on Tuesday says nothing about Wednesday morning. Keyed by date
 * for the same reason, and old keys are pruned on write so this cannot grow a
 * row per day forever.
 *
 * `localStorage` rather than `sessionStorage`, unlike the basket this replaces.
 * A basket was a thing you were in the middle of; this is a preference, and a
 * preference that evaporates when the tab is closed is not one.
 *
 * Every access is wrapped. Storage throws rather than returning null in Safari
 * with site data blocked, and a private window is not a reason for Today to be
 * a white page.
 */

import type { Meal } from "@calorio/core";

const KEY = "calorio:open-meals";
/** Keep about a fortnight of arrangements. Beyond that nobody is going back. */
const KEEP_DAYS = 14;

type Stored = Record<string, Meal[]>;

function read(): Stored {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? (parsed as Stored) : {};
  } catch {
    return {};
  }
}

/**
 * The meals open on `date`, or null if this day has never been arranged.
 *
 * Null and "none open" are different answers and the caller needs both: an
 * unarranged day opens the meal the clock suggests, while a day somebody
 * deliberately collapsed everything on stays collapsed.
 */
export function openMeals(date: string): Meal[] | null {
  const stored = read()[date];
  return Array.isArray(stored) ? stored : null;
}

export function setOpenMeals(date: string, meals: Meal[]): void {
  try {
    const all = read();
    all[date] = meals;
    // Newest first, then truncated: sorting the keys as strings works because
    // `YYYY-MM-DD` sorts lexically the same way it sorts chronologically.
    const kept = Object.keys(all)
      .sort()
      .slice(-KEEP_DAYS)
      .reduce<Stored>((out, key) => ({ ...out, [key]: all[key]! }), {});
    localStorage.setItem(KEY, JSON.stringify(kept));
  } catch {
    // The arrangement still works for this visit; it just does not survive
    // leaving, which is where this started.
  }
}
